import { useLocation, useNavigate } from "src/router";
import { usePartnerConnectGetInformationQuery } from "src/graphql";
import { useQueryString } from "src/utils/use-query-string";
import { useState } from "react";
import { useUser } from "src/contexts/user-context";
import { captureException } from "src/lib/monitoring";
import { useToast } from "@hightouch/htui";
import { isMcpAuthReturnTo, isValidApiRedirectOnLogin } from "./util";

interface LocationState {
  from?: {
    pathname: string;
  };
}

export const useAuthState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetch: refetchUser } = useUser();
  const [isHandlingSuccessfulOauth, setIsHandlingSuccessfulOauth] =
    useState(false);
  const { toast } = useToast();

  const locationState = location.state as LocationState | undefined;
  const {
    data: { returnTo: queryLocation, error },
  } = useQueryString();
  const returnTo = queryLocation || locationState?.["from"]?.["pathname"];
  const isMcpAuthFlow = isMcpAuthReturnTo(returnTo);
  const match = returnTo?.match(
    /^\/?partner-connect\/([0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12})$/i,
  );
  const partnerConnectionUUID = match?.[1];
  const { data: partnerInformation } = usePartnerConnectGetInformationQuery(
    { uuid: partnerConnectionUUID ?? "" },
    {
      enabled: Boolean(partnerConnectionUUID),
      select: (data) => data.partnerConnectGetInformation,
      suspense: true,
    },
  );

  const microsoftAuthUrl = new URL(
    `${import.meta.env.VITE_API_BASE_URL}/azure/oauth`,
  );

  if (returnTo) {
    microsoftAuthUrl.searchParams.append("returnTo", returnTo);
  }

  const isSignup = location.pathname.includes("signup");

  const handleSuccessfulOauth = async (user: { welcome?: boolean }) => {
    setIsHandlingSuccessfulOauth(true);
    try {
      // For API redirects (e.g. MCP login), navigate immediately before
      // refetching user state. The session cookie is already set by the
      // OAuth POST — refetchUser() would trigger a React re-render that
      // races with this hard navigation.
      if (returnTo && isValidApiRedirectOnLogin(returnTo)) {
        window.location.href = returnTo;
        return;
      }

      await refetchUser();
      if (returnTo) {
        navigate(returnTo);
      } else if (user.welcome) {
        navigate("/welcome");
      } else {
        navigate("/");
      }
    } catch (e) {
      toast({
        id: "oauth-login-failed",
        title: "There was an unexpected error",
        variant: "error",
      });
      captureException(e);
    } finally {
      setIsHandlingSuccessfulOauth(false);
    }
  };

  return {
    error,
    isSignup,
    isMcpAuthFlow,
    returnTo,
    partnerConnectionUUID,
    microsoftAuthUrl: microsoftAuthUrl.toString(),
    partnerInformation,
    handleSuccessfulOauth,
    isHandlingSuccessfulOauth,
  };
};
